// store/persist/prototypes.js
import { Del } from "../../models/Del/Del";
import { DelProject } from "../../models/Del/DelProject";
import { DelEmployee, DelShift } from "../../models/Del/DelEmployee";
import { DelUnit, MiscEquip, RentalEquip } from "../../models/Del/DelUnit";
import { DelProjectUnit } from "../../models/Del/DelProjectUnit";
import { FieldNotes, FieldNoteActivity } from "../../models/Del/FieldNotes";
import { DelClientSignatures } from "../../models/Del/DelClientSignatures";
import { DelSdsReference } from "../../models/Del/DelSdsReference";
import { JsaV1 } from "../../models/Jsa/JsaV1";
import { JsaV2 } from "../../models/Jsa/JsaV2";
import { JsaBasicInfo } from "../../models/Jsa/core/JsaBasicInfo";
import { JsaRacInfo } from "../../models/Jsa/core/JsaRacInfo";
import {
  JsaTrainingInfo,
  JsaTrainingEmployeesInfo,
} from "../../models/Jsa/core/JsaTrainingInfo";
import { JsaEmployeeSignaturesInfo } from "../../models/Jsa/core/JsaEmployeeSignaturesInfo";
import { JsaNonEmployeeSignaturesInfo } from "../../models/Jsa/core/JsaNonEmployeeSignaturesInfo";
import { JsaEmergencyEquipment } from "../../models/Jsa/v1/JsaEmergencyEquipment";
import { JsaPermitInfo } from "../../models/Jsa/v1/JsaPermitInfo";
import { JsaPpeInfo } from "../../models/Jsa/v1/JsaPpeInfo";
import { JsaHazCom } from "../../models/Jsa/v1/JsaHazCom";
import { JsaConfinedSpace } from "../../models/Jsa/v1/JsaConfinedSpace";
import {
  JsaFallProtection,
  JsaFallProtectionInspectionList,
} from "../../models/Jsa/v1/JsaFallProtection";
import { JsaHazardsV2 } from "../../models/Jsa/v2/JsaHazardsV2";
import { JsaControlMeasuresV2 } from "../../models/Jsa/v2/JsaControlMeasuresV2";
import {
  WasteDisposal,
  WasteDisposalBasicInfo,
  WasteDisposalContainerInfo,
  WasteDisposalConstituents,
} from "../../models/WasteDisposal/WasteDisposal";
import { SamplingEvent } from "../../models/SamplingEvent/SamplingEvent";
import { DailySummary } from "../../models/SamplingEvent/DailySummary";
import {
  Wells,
  SamplingEmployeesSignature,
  FieldMeasurements,
} from "../../models/SamplingEvent/Wells";
import { WaterCalibrationLog } from "../../models/SamplingEvent/WaterCalibrationLog";
import { EmployeeFieldNotes } from "../../models/SamplingEvent/FieldNotes";
import {
  SamplingGroup,
  SamplingGroupEmployees,
  SamplingGroupWells,
} from "../../models/SamplingEvent/SamplingGroup";
import { NearMiss } from "../../models/SafetyForms/nearMiss/NearMiss";
import {
  SafetyAuditChecklist,
  SafetyAuditChecklistEmployees,
} from "../../models/SafetyForms/safetyAudit/SafetyAudit";
import { FormPhotos } from "../../models/FormPhotos/FormPhotos";

export const restoreWasteDisposalPrototypes = (wd) => {
  if (!wd) return;
  Object.setPrototypeOf(wd, WasteDisposal.prototype);
  wd.basicInfo &&
    Object.setPrototypeOf(wd.basicInfo, WasteDisposalBasicInfo.prototype);
  wd.containerInfo?.forEach((c) =>
    Object.setPrototypeOf(c, WasteDisposalContainerInfo.prototype)
  );
  wd.constituents?.forEach((c) =>
    Object.setPrototypeOf(c, WasteDisposalConstituents.prototype)
  );
};

export const restoreJsaPrototypes = (jsa, isV1 = false) => {
  if (!jsa) return;
  Object.setPrototypeOf(jsa, isV1 ? JsaV1.prototype : JsaV2.prototype);
  jsa.jsaBasicInfo &&
    Object.setPrototypeOf(jsa.jsaBasicInfo, JsaBasicInfo.prototype);
  jsa.jsaRacInfo && Object.setPrototypeOf(jsa.jsaRacInfo, JsaRacInfo.prototype);
  jsa.jsaTrainingInfo &&
    Object.setPrototypeOf(jsa.jsaTrainingInfo, JsaTrainingInfo.prototype);
  jsa.jsaTrainingInfo?.jsaTrainingEmployeesInfo?.forEach((t) =>
    Object.setPrototypeOf(t, JsaTrainingEmployeesInfo.prototype)
  );
  jsa.jsaEmployeeSignaturesInfo?.forEach((s) =>
    Object.setPrototypeOf(s, JsaEmployeeSignaturesInfo.prototype)
  );
  jsa.jsaNonEmployeeSignaturesInfo?.forEach((s) =>
    Object.setPrototypeOf(s, JsaNonEmployeeSignaturesInfo.prototype)
  );

  if (isV1) {
    jsa.jsaEmergencyEquipment &&
      Object.setPrototypeOf(
        jsa.jsaEmergencyEquipment,
        JsaEmergencyEquipment.prototype
      );
    jsa.jsaPermitInfo &&
      Object.setPrototypeOf(jsa.jsaPermitInfo, JsaPermitInfo.prototype);
    jsa.jsaPpeInfo &&
      Object.setPrototypeOf(jsa.jsaPpeInfo, JsaPpeInfo.prototype);
    jsa.jsaHazCom && Object.setPrototypeOf(jsa.jsaHazCom, JsaHazCom.prototype);
    jsa.jsaConfinedSpace &&
      Object.setPrototypeOf(jsa.jsaConfinedSpace, JsaConfinedSpace.prototype);
    jsa.jsaFallProtection &&
      Object.setPrototypeOf(jsa.jsaFallProtection, JsaFallProtection.prototype);
    jsa.jsaFallProtection?.fallProtectionInspectionList?.forEach((i) =>
      Object.setPrototypeOf(i, JsaFallProtectionInspectionList.prototype)
    );
  } else {
    jsa.jsaHazardsV2 &&
      Object.setPrototypeOf(jsa.jsaHazardsV2, JsaHazardsV2.prototype);
    jsa.jsaControlMeasuresV2 &&
      Object.setPrototypeOf(
        jsa.jsaControlMeasuresV2,
        JsaControlMeasuresV2.prototype
      );
  }
};

export const restoreDelPrototypes = (cached) => {
  if (!cached) return null;
  Object.setPrototypeOf(cached, Del.prototype);
  cached.projectInfo &&
    Object.setPrototypeOf(cached.projectInfo, DelProject.prototype);
  cached.employees?.forEach((e) => {
    Object.setPrototypeOf(e, DelEmployee.prototype);
    e.shifts?.forEach((s) => Object.setPrototypeOf(s, DelShift.prototype));
  });
  cached.units?.forEach((u) => Object.setPrototypeOf(u, DelUnit.prototype));
  cached.miscUnits?.forEach((m) =>
    Object.setPrototypeOf(m, MiscEquip.prototype)
  );
  cached.rentalUnits?.forEach((r) =>
    Object.setPrototypeOf(r, RentalEquip.prototype)
  );
  cached.projectSpecificUnits?.forEach((p) =>
    Object.setPrototypeOf(p, DelProjectUnit.prototype)
  );
  if (cached.fieldNotes) {
    Object.setPrototypeOf(cached.fieldNotes, FieldNotes.prototype);
    cached.fieldNotes.activities?.forEach((a) =>
      Object.setPrototypeOf(a, FieldNoteActivity.prototype)
    );
  }
  if (cached.clientSignatures) {
    cached.clientSignatures.forEach((c) =>
      Object.setPrototypeOf(c, DelClientSignatures.prototype)
    );
  }
  // Older cached DELs predate sdsReferences; the key is optional
  cached.sdsReferences?.forEach((r) =>
    Object.setPrototypeOf(r, DelSdsReference.prototype)
  );
  restoreJsaPrototypes(cached.jsa, cached.projectInfo?.jsaVersion === 1);
  restoreWasteDisposalPrototypes(cached.wasteDisposal);
  return cached;
};

export const restoreAllActiveDelPrototypes = (state) => {
  // drafts
  if (state.activeDelDrafts?.currentDel) {
    try {
      state.activeDelDrafts.currentDel = restoreDelPrototypes(
        state.activeDelDrafts.currentDel
      );
    } catch (e) {
      console.error("Failed to restore prototypes for activeDelDrafts", e);
      state.activeDelDrafts.currentDel = null;
    }
  }
  // submits
  if (state.activeDelSubmits?.currentDel) {
    try {
      state.activeDelSubmits.currentDel = restoreDelPrototypes(
        state.activeDelSubmits.currentDel
      );
    } catch (e) {
      console.error("Failed to restore prototypes for activeDelSubmits", e);
      state.activeDelSubmits.currentDel = null;
    }
  }
  // approvals
  if (state.activeDelApprovals?.awaiting?.currentDel) {
    try {
      state.activeDelApprovals.awaiting.currentDel = restoreDelPrototypes(
        state.activeDelApprovals.awaiting.currentDel
      );
    } catch (e) {
      console.error(
        "Failed to restore prototypes for activeDelApprovals.awaiting",
        e
      );
      state.activeDelApprovals.awaiting.currentDel = null;
    }
  }
  if (state.activeDelApprovals?.approved?.currentDel) {
    try {
      state.activeDelApprovals.approved.currentDel = restoreDelPrototypes(
        state.activeDelApprovals.approved.currentDel
      );
    } catch (e) {
      console.error(
        "Failed to restore prototypes for activeDelApprovals.approved",
        e
      );
      state.activeDelApprovals.approved.currentDel = null;
    }
  }
  return state;
};

export const restoreSamplingEventPrototypes = (cached) => {
  if (!cached) return null;
  try {
    Object.setPrototypeOf(cached, SamplingEvent.prototype);

    // Groups
    cached.samplingGroups?.forEach((g) => {
      Object.setPrototypeOf(g, SamplingGroup.prototype);
      g.employees?.forEach((e) =>
        Object.setPrototypeOf(e, SamplingGroupEmployees.prototype)
      );
      g.wells?.forEach((w) =>
        Object.setPrototypeOf(w, SamplingGroupWells.prototype)
      );
    });

    // Wells and nested
    cached.samplingEventWells?.forEach((w) => {
      Object.setPrototypeOf(w, Wells.prototype);
      w.fieldMeasurements?.forEach((m) =>
        Object.setPrototypeOf(m, FieldMeasurements.prototype)
      );
      w.employeeSignatures?.forEach((s) =>
        Object.setPrototypeOf(s, SamplingEmployeesSignature.prototype)
      );
    });

    // Daily summaries
    cached.dailySummaries?.forEach((d) =>
      Object.setPrototypeOf(d, DailySummary.prototype)
    );

    // Water calibration log
    cached.waterCalibrationLog?.forEach((l) =>
      Object.setPrototypeOf(l, WaterCalibrationLog.prototype)
    );

    // Field notes
    cached.fieldNotes?.forEach((n) =>
      Object.setPrototypeOf(n, EmployeeFieldNotes.prototype)
    );

    // JSAs
    cached.jsa?.forEach((j) => restoreJsaPrototypes(j, false));
  } catch (e) {
    console.error("Failed to restore SamplingEvent prototypes", e);
    return null;
  }
  return cached;
};

export const restoreActiveSamplingEventPrototypes = (state) => {
  if (state.activeSamplingEvent?.currentEvent) {
    try {
      state.activeSamplingEvent.currentEvent = restoreSamplingEventPrototypes(
        state.activeSamplingEvent.currentEvent
      );
    } catch (e) {
      console.error("Failed to restore prototypes for activeSamplingEvent", e);
      state.activeSamplingEvent.currentEvent = null;
    }
  }
  return state;
};

export const restoreSafetyAuditPrototypes = (cached) => {
  if (!cached) return null;
  try {
    Object.setPrototypeOf(cached, SafetyAuditChecklist.prototype);
    cached.employees?.forEach((e) =>
      Object.setPrototypeOf(e, SafetyAuditChecklistEmployees.prototype)
    );
    cached.photos?.forEach((p) =>
      Object.setPrototypeOf(p, FormPhotos.prototype)
    );
    // jsa is lazy-loaded; restore only if it was loaded before persistence
    if (cached.jsa) {
      restoreJsaPrototypes(cached.jsa, cached.jsa_version === 1);
    }
  } catch (e) {
    console.error("Failed to restore SafetyAudit prototypes", e);
    return null;
  }
  return cached;
};

export const restoreActiveSafetyAuditPrototypes = (state) => {
  if (state.activeSafetyAuditForm?.currentSafetyAudit) {
    try {
      state.activeSafetyAuditForm.currentSafetyAudit =
        restoreSafetyAuditPrototypes(
          state.activeSafetyAuditForm.currentSafetyAudit
        );
    } catch (e) {
      console.error("Failed to restore prototypes for activeSafetyAuditForm", e);
      state.activeSafetyAuditForm.currentSafetyAudit = null;
    }
  }
  return state;
};

export const restoreNearMissPrototypes = (cached) => {
  if (!cached) return null;
  try {
    Object.setPrototypeOf(cached, NearMiss.prototype);
    cached.photos?.forEach((p) =>
      Object.setPrototypeOf(p, FormPhotos.prototype)
    );
  } catch (e) {
    console.error("Failed to restore NearMiss prototypes", e);
    return null;
  }
  return cached;
};

export const restoreActiveNearMissPrototypes = (state) => {
  if (state.activeNearMiss?.currentNearMiss) {
    try {
      state.activeNearMiss.currentNearMiss = restoreNearMissPrototypes(
        state.activeNearMiss.currentNearMiss
      );
    } catch (e) {
      console.error("Failed to restore prototypes for activeNearMiss", e);
      state.activeNearMiss.currentNearMiss = null;
    }
  }
  return state;
};
